import type { ConfirmationResponse } from "@/features/register/types";
import { AuthApiClient } from "@/features/user/api-client/api-client";
import { map_np_to_np_missing_fields } from "@/features/user/converters";
import { factory_naturalPerson_partial_edit } from "@/features/user/factories";
import { useUser } from "@/features/user/hooks/use-user";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import type { SignupState } from "@/models/signup";
import { type UseMutationOptions, useMutation } from "@tanstack/react-query";
import { Substep } from "../types";

export type UseCompleteAccountProps = {
  signupState: SignupState;
  email: string;
  password: string;
  initialMissingKeys: Substep[];
};

export const useCompleteAccount = (
  options?: UseMutationOptions<ConfirmationResponse, Error, UseCompleteAccountProps>
) => {
  const { useEditProfileMissingFields } = useUser();
  const { mutateAsync: editProfile } = useEditProfileMissingFields();
  const { getLanguage } = usei18n();

  return useMutation<ConfirmationResponse, Error, UseCompleteAccountProps>({
    mutationFn: async ({ signupState, initialMissingKeys }: UseCompleteAccountProps) => {
      const staticData = await AuthApiClient.getStaticData("fr");
      // Edit profile
      const user = await AuthApiClient.profile();
      const naturalPersonEdit = factory_naturalPerson_partial_edit(
        user.naturalPerson,
        signupState,
        staticData,
        initialMissingKeys
      );

      const naturalEntity = map_np_to_np_missing_fields(naturalPersonEdit, getLanguage());

      await editProfile(naturalEntity);

      return { success: true, message: "" };
    },
    ...options
  });
};
